import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Plus, Star, Trash2, Edit, LogOut } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

function LegalEntities({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEntity, setEditingEntity] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    bin_iin: '',
    legal_address: '',
    contact_person: '',
    phone: '',
    bank_name: '',
    bank_account: '',
    is_default: false
  });

  useEffect(() => {
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      const response = await axios.get(`${API}/legal-entities`, { headers: getAuthHeaders() });
      setEntities(response.data);
    } catch (error) {
      toast.error(t('admin.failed') || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEntity) {
        await axios.put(`${API}/legal-entities/${editingEntity.id}`, formData, { headers: getAuthHeaders() });
        toast.success(t('legalEntities.updated') || 'Юрлицо обновлено');
      } else {
        await axios.post(`${API}/legal-entities`, formData, { headers: getAuthHeaders() });
        toast.success(t('legalEntities.added') || 'Юрлицо добавлено');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.failed') || 'Ошибка сохранения');
    }
  };

  const handleDelete = async (entityId) => {
    if (!window.confirm(t('legalEntities.confirmDelete') || 'Удалить это юрлицо?')) return;
    
    try {
      await axios.delete(`${API}/legal-entities/${entityId}`, { headers: getAuthHeaders() });
      toast.success(t('legalEntities.deleted') || 'Юрлицо удалено');
      fetchEntities();
    } catch (error) {
      toast.error(t('admin.failed') || 'Ошибка удаления');
    }
  };

  const handleSetDefault = async (entityId) => {
    try {
      await axios.put(`${API}/legal-entities/${entityId}/set-default`, {}, { headers: getAuthHeaders() });
      toast.success(t('legalEntities.defaultSet') || 'Юрлицо по умолчанию установлено');
      fetchEntities();
    } catch (error) {
      toast.error(t('admin.failed') || 'Ошибка');
    }
  };

  const handleEdit = (entity) => {
    setEditingEntity(entity);
    setFormData({
      company_name: entity.company_name || '',
      bin_iin: entity.bin_iin || '',
      legal_address: entity.legal_address || '',
      contact_person: entity.contact_person || '',
      phone: entity.phone || '',
      bank_name: entity.bank_name || '',
      bank_account: entity.bank_account || '',
      is_default: entity.is_default || false
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEntity(null);
    setFormData({
      company_name: '',
      bin_iin: '',
      legal_address: '',
      contact_person: '',
      phone: '',
      bank_name: '',
      bank_account: '',
      is_default: false
    });
  };

  const handleOpenNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Redirect if not B2B
  if (user?.role !== 'B2B') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">{t('legalEntities.b2bOnly') || 'Эта страница доступна только для бизнес-аккаунтов'}</p>
          <Link to={`/${currentLang}`}>
            <Button className="mt-4 bg-emerald-700 hover:bg-emerald-800">
              {t('auth.backToCatalog')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={`/${currentLang}`} className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="Ainanan" 
              className="h-10 object-contain"
            />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">
        <Link to={`/${currentLang}`} className="inline-flex items-center text-stone-600 hover:text-stone-800 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('auth.backToCatalog')}
        </Link>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Link to={`/${currentLang}/profile`}>
            <Button variant="outline">{t('profile.title') || 'Профиль'}</Button>
          </Link>
          <Link to={`/${currentLang}/settings`}>
            <Button variant="outline">{t('settings.title') || 'Настройки'}</Button>
          </Link>
          <Link to={`/${currentLang}/orders`}>
            <Button variant="outline">{t('common.myOrders')}</Button>
          </Link>
          <Link to={`/${currentLang}/addresses`}>
            <Button variant="outline">{t('common.myAddresses')}</Button>
          </Link>
          <Link to={`/${currentLang}/legal-entities`}>
            <Button variant="default" className="bg-emerald-700 hover:bg-emerald-800">
              {t('legalEntities.title') || 'Юрлица'}
            </Button>
          </Link>
          {user?.role === 'B2B' && (
            <Link to={`/${currentLang}/b2b/networks`}>
              <Button variant="outline">Мои сети</Button>
            </Link>
          )}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <CardTitle>{t('legalEntities.title') || 'Юридические лица'}</CardTitle>
                  <CardDescription>{t('legalEntities.subtitle') || 'Данные для накладных и счетов'}</CardDescription>
                </div>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={handleOpenNew} className="bg-emerald-700 hover:bg-emerald-800">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('legalEntities.add') || 'Добавить'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingEntity ? (t('legalEntities.edit') || 'Редактировать юрлицо') : (t('legalEntities.addNew') || 'Новое юрлицо')}
                    </DialogTitle>
                    <DialogDescription>
                      {t('legalEntities.fillFields') || 'Заполните реквизиты юридического лица'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Company Name */}
                    <div>
                      <Label>{t('legalEntities.companyName') || 'Название компании'} *</Label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        required
                        placeholder="ТОО «Компания»"
                        className="mt-1"
                      />
                    </div>

                    {/* BIN/IIN */}
                    <div>
                      <Label>{t('legalEntities.binIin') || 'БИН/ИИН'} *</Label>
                      <Input
                        value={formData.bin_iin}
                        onChange={(e) => setFormData({ ...formData, bin_iin: e.target.value })}
                        required
                        placeholder="123456789012"
                        className="mt-1 ym-disable-keys rr-mask"
                        maxLength={12}
                      />
                    </div>

                    {/* Legal Address */}
                    <div>
                      <Label>{t('legalEntities.legalAddress') || 'Юридический адрес'} *</Label>
                      <Input
                        value={formData.legal_address}
                        onChange={(e) => setFormData({ ...formData, legal_address: e.target.value })}
                        required
                        placeholder="г. Алматы, ул. Абая 1, офис 100"
                        className="mt-1"
                      />
                    </div>

                    {/* Contact Person & Phone */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{t('legalEntities.contactPerson') || 'Контактное лицо'}</Label>
                        <Input
                          value={formData.contact_person}
                          onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                          placeholder="Иванов И.И."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>{t('legalEntities.phone') || 'Телефон'}</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+7 727 123 4567"
                          className="mt-1 ym-disable-keys rr-mask"
                        />
                      </div>
                    </div>

                    {/* Bank Details */}
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-3">{t('legalEntities.bankDetails') || 'Банковские реквизиты'}</p>
                      <div className="space-y-3">
                        <div>
                          <Label>{t('legalEntities.bankName') || 'Банк'}</Label>
                          <Input
                            value={formData.bank_name}
                            onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                            placeholder="АО «Kaspi Bank»"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>{t('legalEntities.bankAccount') || 'Номер счёта (IBAN)'}</Label>
                          <Input
                            value={formData.bank_account}
                            onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                            placeholder="KZ00000000000000000"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Default checkbox */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_default"
                        checked={formData.is_default}
                        onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="is_default" className="cursor-pointer">
                        {t('legalEntities.setDefault') || 'Использовать по умолчанию'}
                      </Label>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                        {t('common.cancel') || 'Отмена'}
                      </Button>
                      <Button type="submit" className="flex-1 bg-emerald-700 hover:bg-emerald-800">
                        {editingEntity ? (t('common.save') || 'Сохранить') : (t('legalEntities.add') || 'Добавить')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {entities.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">{t('legalEntities.empty') || 'У вас пока нет сохранённых юрлиц'}</p>
                <Button onClick={handleOpenNew} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('legalEntities.addFirst') || 'Добавить первое юрлицо'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {entities.map(entity => (
                  <div 
                    key={entity.id} 
                    className={`p-4 rounded-lg border ${entity.is_default ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${entity.is_default ? 'bg-emerald-200' : 'bg-gray-100'}`}>
                          <Building2 className={`w-5 h-5 ${entity.is_default ? 'text-emerald-700' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{entity.company_name}</span>
                            {entity.is_default && (
                              <Badge className="bg-emerald-600 text-xs">
                                <Star className="w-3 h-3 mr-1" />
                                {t('legalEntities.default') || 'По умолчанию'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            БИН/ИИН: {entity.bin_iin}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {entity.legal_address}
                          </p>
                          {entity.bank_name && (
                            <p className="text-xs text-gray-400 mt-1">
                              {entity.bank_name} • {entity.bank_account}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!entity.is_default && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSetDefault(entity.id)}
                            title={t('legalEntities.makeDefault') || 'Сделать по умолчанию'}
                          >
                            <Star className="w-4 h-4 text-gray-400" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEdit(entity)}
                        >
                          <Edit className="w-4 h-4 text-gray-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(entity.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default LegalEntities;
